import React from "react"
import { Badge } from "../common/badge"
import { Card } from "../common/card"
import TypingEffect from "./typing-effect"

interface Entry {
  user: string
  ai: {
    answer: {
      error?: boolean
      message: string
      summary?: string
      success?: boolean
      suggestion?: string[]
      required_fields?: string[]
    }
  }
  setText?: React.Dispatch<React.SetStateAction<string>>
}

const ConversationItem = ({ entry, isLast }: { entry: Entry; isLast: boolean }) => {
  const { user, ai, setText } = entry
  const { answer } = ai

  // Render the assistant's response based on its type.

  const renderAssistantResponse = () => {
    // Show error messages in red.

    if (answer.error) {
      return <div style={{ color: "red" }}>{answer.message}</div>
    }

    // Show required fields in red with a message.

    if (answer.message && answer.required_fields) {
      return (
        <div>
          <span className='flex flex-col gap-1'>
            <p>{isLast ? <TypingEffect text={answer.message} /> : answer.message}</p>
            <span className='flex flex-wrap gap-2'>
              {answer.required_fields.map((x: string) => (
                <Badge key={x} variant='destructive' className='m-2 w-fit'>
                  <TypingEffect text={x} />
                </Badge>
              ))}
            </span>
          </span>
        </div>
      )
    }
    // show general messages in a normal way.
    if (answer.message && !answer.summary && !answer.success) {
      return <p>{isLast ? <TypingEffect text={answer.message} /> : answer.message}</p>
    }
    // Show summary responses inside a preformatted block.
    if (answer.summary) {
      return (
        <div className='flex flex-col gap-2'>
          <p>{isLast ? <TypingEffect text={answer.message} /> : answer.message}</p>
          <Card className='whitespace-pre-wrap bg-[#f0f0f0] p-2.5'>
            <TypingEffect
              text={Object.entries(answer.summary)
                .map(([key, value]) => `${key}: ${value}`)
                .join("\n")}
            />
          </Card>
        </div>
      )
    }

    // Handle success messages with suggestions.
    // if (answer.success && (answer.suggestion ?? []).length > 0) {
    //   return (
    //     <div style={{ color: "green" }}>
    //       <p>{isLast ? <TypingEffect text={answer.message} /> : answer.message}</p>
    //       {answer.suggestion && answer.suggestion.length > 0 && (
    //         <span className='flex flex-row gap-1'>
    //           {answer.suggestion.map((s, index) => (
    //             // <li key={index}>{s}</li>
    //             <Badge key={index} variant='outline' className='m-2 w-fit'>
    //               <TypingEffect text={s} />
    //             </Badge>
    //           ))}
    //         </span>
    //       )}
    //     </div>
    //   )
    // }
    // For success responses, optionally wrap the message in the typing effect.
    if (answer.success) {
      return (
        <div className='text-black'>
          <p>{isLast ? <TypingEffect text={answer.message} /> : answer.message}</p>
          {answer.suggestion && answer.suggestion.length > 0 && (
            <ol>
              {answer.suggestion.map((s, index) => (
                <li
                  className='ml-7 cursor-pointer text-green-800'
                  onClick={() => setText?.(s)}
                  key={index}
                >
                  {"- "}
                  {s}
                </li>
              ))}
            </ol>
          )}
        </div>
      )
    }
    return null
  }

  return (
    <div
      style={{
        marginBottom: "20px",
        paddingBottom: "10px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* User query row: aligned to right */}
      <div className='max-w-[80%] self-end rounded-[10px] bg-[#f4f4f4] p-2.5 text-right'>
        {user}
      </div>
      {/* Assistant response row: aligned to left */}
      <div className='mt-1.5 max-w-[80%] self-start rounded-[10px] border border-gray-200 bg-[#F9F9F9] p-2.5 text-left'>
        {renderAssistantResponse()}
      </div>
    </div>
  )
}

export default ConversationItem
